import type { SurfaceId } from './types';

/**
 * Per-surface tyre/terrain parameters for a gravel-spec tyre (the car's tyre compound applies
 * multipliers on top). Values are DERIVED targets — see tests/physics/targets.md for the reasoning:
 *  - loose surfaces: lower peak µ, peak at larger slip, and a flat curve after the peak (high
 *    `falloff`) so a sustained slide stays controllable with the throttle;
 *  - sealed surfaces: higher peak at small slip with a steeper drop into sliding.
 */
export interface SurfaceParams {
  id: SurfaceId;
  /** Peak friction coefficient (longitudinal) for a gravel tyre at nominal load. */
  mu: number;
  /** Slip ratio at peak longitudinal force. */
  slipPeak: number;
  /** Slip angle at peak lateral force (degrees). */
  slipAnglePeakDeg: number;
  /** Fully-sliding force as a fraction of peak (asymptote of the curve). */
  falloff: number;
  /** Rolling-resistance coefficient (force / normal load). */
  rollingResistance: number;
  /** Extra speed-dependent drag per wheel in contact: N per (m/s)^2 per kN of load (water, deep mud). */
  wadingDrag: number;
  /** Visual/audio tags. */
  dust: number;
  spray: 'none' | 'gravel' | 'mud' | 'water' | 'grass';
  sound: 'tarmac' | 'gravel' | 'mud' | 'water' | 'grass' | 'rock';
  /** Road-noise vertical amplitude scale (bumpiness felt through suspension), metres. */
  roughness: number;
}

export const SURFACES: Record<SurfaceId, SurfaceParams> = {
  tarmac: { id: 'tarmac', mu: 1.05, slipPeak: 0.1, slipAnglePeakDeg: 7.5, falloff: 0.78, rollingResistance: 0.013, wadingDrag: 0, dust: 0, spray: 'none', sound: 'tarmac', roughness: 0.004 },
  concrete: { id: 'concrete', mu: 1.0, slipPeak: 0.1, slipAnglePeakDeg: 7.5, falloff: 0.78, rollingResistance: 0.013, wadingDrag: 0, dust: 0, spray: 'none', sound: 'tarmac', roughness: 0.003 },
  gravel: { id: 'gravel', mu: 0.8, slipPeak: 0.14, slipAnglePeakDeg: 11, falloff: 0.9, rollingResistance: 0.028, wadingDrag: 0, dust: 1, spray: 'gravel', sound: 'gravel', roughness: 0.012 },
  loose: { id: 'loose', mu: 0.64, slipPeak: 0.2, slipAnglePeakDeg: 14, falloff: 0.94, rollingResistance: 0.05, wadingDrag: 0.0, dust: 1, spray: 'gravel', sound: 'gravel', roughness: 0.016 },
  mud: { id: 'mud', mu: 0.48, slipPeak: 0.22, slipAnglePeakDeg: 13, falloff: 0.92, rollingResistance: 0.07, wadingDrag: 0.004, dust: 0, spray: 'mud', sound: 'mud', roughness: 0.01 },
  wet: { id: 'wet', mu: 0.68, slipPeak: 0.15, slipAnglePeakDeg: 11, falloff: 0.9, rollingResistance: 0.034, wadingDrag: 0, dust: 0, spray: 'mud', sound: 'mud', roughness: 0.012 },
  water: { id: 'water', mu: 0.55, slipPeak: 0.18, slipAnglePeakDeg: 12, falloff: 0.9, rollingResistance: 0.05, wadingDrag: 0.03, dust: 0, spray: 'water', sound: 'water', roughness: 0.01 },
  grass: { id: 'grass', mu: 0.52, slipPeak: 0.18, slipAnglePeakDeg: 12, falloff: 0.86, rollingResistance: 0.06, wadingDrag: 0, dust: 0.1, spray: 'grass', sound: 'grass', roughness: 0.02 },
  rock: { id: 'rock', mu: 0.85, slipPeak: 0.12, slipAnglePeakDeg: 9, falloff: 0.84, rollingResistance: 0.02, wadingDrag: 0, dust: 0.2, spray: 'gravel', sound: 'rock', roughness: 0.03 },
};

export const SURFACE_IDS = Object.keys(SURFACES) as SurfaceId[];

/** Surface index (stable small int) for compact lookup tables. */
export const SURFACE_INDEX: Record<SurfaceId, number> = Object.fromEntries(SURFACE_IDS.map((s, i) => [s, i])) as Record<SurfaceId, number>;

/** A world query that tells the tyre model what it is standing on. */
export interface SurfaceQuery {
  surfaceAt(x: number, z: number): SurfaceId;
}

export const uniformSurface = (id: SurfaceId): SurfaceQuery => ({ surfaceAt: () => id });
